import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Container,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";

import {
  faKey,
  faPlus,
  faSearch,
  faSync,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import AdvanceTable from "../../components/advanceTable/AdvanceTable";
import AdvanceTablePagination from "../../components/advanceTable/AdvanceTablePagination";
import AdvanceTableWrapper from "../../components/advanceTable/AdvanceTableWrapper";
import ApiKeyModal from "../../components/admin/apiKeys/ApiKeyModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import InformationModal from "../../components/InformationModal";

import { apiKeysApi } from "../../services/apiKeyServices";

const initConfirmationModal = {
  isOpen: false,
  onSubmit: null,
  onClose: null,
  title: "",
  body: "",
};

const SEARCH_DEBOUNCE_MS = 350;

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [sortState, setSortState] = useState({ sortBy: null, direction: null });

  const [confirmationModal, setConfirmationModal] = useState(
    initConfirmationModal,
  );

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setLoading(true);
    apiKeysApi
      .getApiKeys({
        search: debouncedSearch.trim() || undefined,
        page,
        pageSize,
      })
      .then((res) => {
        setLoading(false);
        setApiKeys(Array.isArray(res?.keys) ? res.keys : []);
        setTotal(typeof res?.total === "number" ? res.total : 0);
      })
      .catch(() => {
        setLoading(false);
        setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error loading API keys.",
        });
      });
  }, [refresh, debouncedSearch, page, pageSize]);

  const onSort = useCallback(([data]) => {
    if (data) {
      setSortState({ sortBy: data.id, direction: data.desc ? "desc" : "asc" });
    } else {
      setSortState({ sortBy: null, direction: null });
    }
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);

  const onDelete = (apiKey) => {
    setConfirmationModal({
      isOpen: true,
      onSubmit: async () => {
        try {
          await apiKeysApi.deleteApiKey(apiKey);
          setConfirmationModal(initConfirmationModal);
          setRefresh((r) => !r);
        } catch (err) {
          setConfirmationModal(initConfirmationModal);
          setInformationModal({
            isOpen: true,
            title: "Could not revoke API key",
            body:
              err?.response?.data?.message ||
              err?.response?.data?.[0]?.msg ||
              "The key was not revoked. Try again or refresh the page.",
          });
        }
      },
      onClose: () => setConfirmationModal(initConfirmationModal),
      title: "Revoke API Key",
      body: `<p class="text-center">Are you sure you want to revoke this API key?</p>
        <strong>${apiKey.name}</strong><br/>
        <small class="text-muted">This action cannot be undone. Any integrations using this key will stop working.</small>`,
      confirmColor: "danger",
    });
  };

  const columns = [
    {
      accessor: "name",
      Header: "Name",
      headerProps: { className: "text-truncate" },
      Cell: (rowData) => {
        const { name } = rowData.row.original;
        return (
          <span className="d-flex align-items-center">
            <FontAwesomeIcon icon={faKey} className="mr-2 text-muted" />
            {name || "-"}
          </span>
        );
      },
    },
    {
      accessor: "prefix",
      Header: "Key Prefix",
      headerProps: { className: "text-truncate" },
      Cell: (rowData) => {
        const { prefix } = rowData.row.original;
        return prefix ? (
          <code className="text-muted">{prefix}••••••••</code>
        ) : (
          "-"
        );
      },
    },
    {
      accessor: "createdAt",
      Header: "Created",
      headerProps: { className: "text-truncate" },
      Cell: (rowData) => {
        const { createdAt } = rowData.row.original;
        if (!createdAt) return "-";
        return new Date(createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      },
    },
    {
      accessor: "lastUsedAt",
      Header: "Last Used",
      headerProps: { className: "text-truncate" },
      Cell: (rowData) => {
        const { lastUsedAt } = rowData.row.original;
        if (!lastUsedAt) return <span className="text-muted">Never</span>;
        return new Date(lastUsedAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      },
    },
    {
      accessor: "id",
      Header: "Actions",
      disableSortBy: true,
      headerProps: { className: "text-truncate text-center" },
      cellProps: { className: "text-truncate text-center" },
      Cell: (rowData) => (
        <Button
          size="sm"
          className="rounded"
          color="danger"
          onClick={() => onDelete(rowData.row.original)}
        >
          <span>Revoke</span>
          <FontAwesomeIcon icon={faTrash} className="ml-2" />
        </Button>
      ),
    },
  ];

  return (
    <Container fluid className="h-100">
      <div className="w-100">
        <AdvanceTableWrapper
          columns={columns}
          data={apiKeys}
          pageSize={Math.max(apiKeys.length, pageSize)}
          sortable
          onSort={onSort}
          defaultSort={sortState}
        >
          <Card className="mb-3 w-100 overflow-x-auto">
            <CardHeader className="d-flex">
              <div className="text-dark flex-grow-1 d-flex align-items-center">
                <h3 className="mb-0">API Keys</h3>
                <small className="text-muted ml-2 pt-1">({total})</small>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <InputGroup size="m" className="mr-3">
                  <Input
                    maxLength="50"
                    placeholder="Search for.."
                    value={search}
                    onChange={(evt) => setSearch(evt.target.value)}
                  />
                  <InputGroupText className="search-jobs input-group-text bg-primary text-white border-left-0 border-primary">
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroupText>
                </InputGroup>
                <Button
                  size="sm"
                  className="mr-3 rounded-circle custom-rounded-button d-flex"
                  color="primary"
                  onClick={() => setCreateModal(true)}
                  title="Create API Key"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
                <Button
                  size="sm"
                  className="rounded-circle custom-rounded-button d-flex"
                  color="primary"
                  onClick={() => setRefresh((r) => !r)}
                  title="Refresh"
                >
                  <FontAwesomeIcon icon={faSync} size="sm" />
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <AdvanceTable
                table
                isLoading={loading}
                headerClassName="text-muted small"
                tableProps={{
                  striped: true,
                  className: "mb-0",
                }}
              />
            </CardBody>
            <CardFooter>
              <AdvanceTablePagination
                totalCount={total}
                pageCount={totalPages}
                currentPage={page - 1}
                onPageChange={(next) => setPage(next)}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  const next = Math.min(100, Math.max(1, Number(size) || 10));
                  setPageSize(next);
                  setPage(1);
                }}
              />
            </CardFooter>
          </Card>
        </AdvanceTableWrapper>
      </div>

      {createModal ? (
        <ApiKeyModal
          onClose={() => setCreateModal(false)}
          onSubmit={() => {
            setCreateModal(false);
            setRefresh((r) => !r);
          }}
        />
      ) : confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : informationModal.isOpen ? (
        <InformationModal
          {...informationModal}
          onClose={() => setInformationModal({ isOpen: false })}
        />
      ) : null}
    </Container>
  );
};

export default ApiKeys;
